// Requêtes du portail citoyen (TanStack Query). Toutes les clés commencent par « citoyen » :
// la déconnexion de l'usager les retire du cache. Les routes publiques sont lues sans jeton.
import type { LectureNotificationUsager, ListeNotificationsUsager } from '@cc/contrats';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { requete } from '../../api/client';
import { useSessionUsager } from './session/FournisseurSessionUsager';
import type {
  AgendaPublic,
  Catalogue,
  ConversationDossier,
  CreneauxSemaine,
  FicheDemarche,
  FicheDossierUsager,
  Liste,
  PieceCoffre,
  ReclamationUsager,
  RendezVousUsager,
  Representation,
  ResumeDossierUsager,
  VerificationPublique,
} from './types';

export const CLES = {
  catalogue: (code: string) => ['citoyen', 'catalogue', code] as const,
  demarche: (code: string, demarche: string) => ['citoyen', 'demarche', code, demarche] as const,
  dossiers: ['citoyen', 'dossiers'] as const,
  dossier: (id: string) => ['citoyen', 'dossier', id] as const,
  conversation: (id: string) => ['citoyen', 'conversation', id] as const,
  pieces: ['citoyen', 'pieces'] as const,
  representations: ['citoyen', 'representations'] as const,
  rendezVous: ['citoyen', 'rendez-vous'] as const,
  agendas: (code: string) => ['citoyen', 'agendas', code] as const,
  creneaux: (code: string, agenda: string, semaine: string | null) => ['citoyen', 'creneaux', code, agenda, semaine] as const,
  reclamations: ['citoyen', 'reclamations'] as const,
  notifications: ['citoyen', 'notifications'] as const,
};

export function useCatalogue(code: string | null) {
  return useQuery({
    queryKey: CLES.catalogue(code ?? ''),
    queryFn: ({ signal }) => requete<Catalogue>(`/public/collectivites/${encodeURIComponent(code as string)}/demarches`, { signal }),
    enabled: Boolean(code),
    staleTime: 60_000,
  });
}

export function useFicheDemarche(code: string | null, demarche: string | null) {
  return useQuery({
    queryKey: CLES.demarche(code ?? '', demarche ?? ''),
    queryFn: ({ signal }) => requete<FicheDemarche>(`/public/collectivites/${encodeURIComponent(code as string)}/demarches/${encodeURIComponent(demarche as string)}`, { signal }),
    enabled: Boolean(code && demarche),
    staleTime: 60_000,
  });
}

export function useVerification(code: string | null) {
  return useQuery({
    queryKey: ['citoyen', 'verification', code],
    queryFn: ({ signal }) => requete<VerificationPublique>(`/public/verification/${encodeURIComponent(code as string)}`, { signal }),
    enabled: Boolean(code),
    retry: false,
    staleTime: 0,
  });
}

function useConnecte() {
  return useSessionUsager().etat === 'connecte';
}

export function useMesDossiers() {
  const connecte = useConnecte();
  return useQuery({
    queryKey: CLES.dossiers,
    queryFn: ({ signal }) => requete<Liste<ResumeDossierUsager>>('/usager/dossiers', { signal }).then((r) => r.elements),
    enabled: connecte,
  });
}

export function useDossierUsager(id: string | null) {
  const connecte = useConnecte();
  return useQuery({
    queryKey: CLES.dossier(id ?? ''),
    queryFn: ({ signal }) => requete<FicheDossierUsager>(`/usager/dossiers/${id}`, { signal }),
    enabled: connecte && Boolean(id),
    retry: false,
  });
}

/**
 * Conversation d'une demande déposée (messagerie du socle). La lecture marque les messages lus :
 * « non_lus » vaut le nombre de messages nouveaux au moment de cette lecture. Tant que l'écran est
 * ouvert, la conversation est relue toutes les trente secondes (réponse du service). La requête
 * n'est pas annulable : une lecture annulée côté client (double montage en développement) serait
 * quand même traitée par le serveur, qui marquerait lu avant la lecture suivante.
 */
export function useConversation(id: string | null, actif = true) {
  const connecte = useConnecte();
  return useQuery({
    queryKey: CLES.conversation(id ?? ''),
    queryFn: () => requete<ConversationDossier>(`/usager/dossiers/${id}/conversation`),
    enabled: connecte && actif && Boolean(id),
    retry: false,
    staleTime: 0,
    refetchInterval: 30_000,
  });
}

/** Message de l'usager au service ; les pièces sont des documents de la demande (identifiants de la fiche). */
export function envoyerMessage(id: string, corps: { contenu: string; pieces: string[] }) {
  return requete<{ id: string }>(`/usager/dossiers/${id}/conversation`, { methode: 'POST', corps });
}

export interface LigneConversation {
  dossier: ResumeDossierUsager;
  /** Conversation lue dans la fiche de la demande ; null tant que la fiche n'est pas chargée. */
  conversation: ConversationDossier | null;
  erreur: unknown;
}

/**
 * Conversations de toutes les demandes déposées, lues dans la fiche de chaque demande (une requête
 * par demande, partagée avec la fiche de suivi) : la fiche donne les non-lus sans les marquer lus,
 * contrairement à la route de conversation. Sert à l'écran Messages et au compteur de la navigation.
 */
export function useConversationsDesDossiers() {
  const connecte = useConnecte();
  const dossiers = useMesDossiers();
  const deposes = useMemo(() => (dossiers.data ?? []).filter((d) => d.statut.code !== 'brouillon'), [dossiers.data]);
  const fiches = useQueries({
    queries: deposes.map((d) => ({
      queryKey: CLES.dossier(d.id),
      queryFn: ({ signal }: { signal: AbortSignal }) => requete<FicheDossierUsager>(`/usager/dossiers/${d.id}`, { signal }),
      enabled: connecte,
      retry: false,
      staleTime: 30_000,
    })),
  });
  const lignes: LigneConversation[] = deposes.map((d, i) => ({ dossier: d, conversation: fiches[i]?.data?.conversation ?? null, erreur: fiches[i]?.error ?? null }));
  const chargement = dossiers.isPending || fiches.some((f) => f.isPending);
  const nonLus = connecte && dossiers.data ? lignes.reduce((n, l) => n + (l.conversation?.non_lus ?? 0), 0) : null;
  return { dossiers, lignes, nonLus, chargement };
}

/** Total des messages non lus de l'usager connecté ; null hors connexion ou avant la première réponse. */
export function useNonLusMessages(): number | null {
  return useConversationsDesDossiers().nonLus;
}

export function useCoffre() {
  const connecte = useConnecte();
  return useQuery({
    queryKey: CLES.pieces,
    queryFn: ({ signal }) => requete<Liste<PieceCoffre>>('/usager/pieces', { signal }).then((r) => r.elements),
    enabled: connecte,
  });
}

export function useRepresentations() {
  const connecte = useConnecte();
  return useQuery({
    queryKey: CLES.representations,
    queryFn: ({ signal }) => requete<Liste<Representation>>('/usager/representations', { signal }).then((r) => r.elements),
    enabled: connecte,
  });
}

export function useMesRendezVous() {
  const connecte = useConnecte();
  return useQuery({
    queryKey: CLES.rendezVous,
    queryFn: ({ signal }) => requete<Liste<RendezVousUsager>>('/usager/rendez-vous', { signal }).then((r) => r.elements),
    enabled: connecte,
  });
}

export function useAgendas(code: string | null) {
  return useQuery({
    queryKey: CLES.agendas(code ?? ''),
    queryFn: ({ signal }) => requete<Liste<AgendaPublic>>(`/public/collectivites/${encodeURIComponent(code as string)}/agendas`, { signal }).then((r) => r.elements),
    enabled: Boolean(code),
    staleTime: 60_000,
  });
}

export function useCreneaux(code: string | null, agenda: string | null, semaine: string | null) {
  return useQuery({
    queryKey: CLES.creneaux(code ?? '', agenda ?? '', semaine),
    queryFn: ({ signal }) =>
      requete<CreneauxSemaine>(`/public/collectivites/${encodeURIComponent(code as string)}/agendas/${agenda}/creneaux`, { signal, parametres: { semaine } }),
    enabled: Boolean(code && agenda),
    staleTime: 15_000,
  });
}

export function useMesReclamations() {
  const connecte = useConnecte();
  return useQuery({
    queryKey: CLES.reclamations,
    queryFn: ({ signal }) => requete<Liste<ReclamationUsager>>('/usager/reclamations', { signal }).then((r) => r.elements),
    enabled: connecte,
  });
}

/** Notifications dans l'application du compte (toutes collectivités), partagées par l'en-tête (non lues) et la page. */
export function useNotificationsUsager() {
  const connecte = useConnecte();
  return useQuery({
    queryKey: CLES.notifications,
    queryFn: ({ signal }) => requete<ListeNotificationsUsager>('/usager/notifications', { signal, parametres: { limite: 50 } }),
    enabled: connecte,
    staleTime: 30_000,
  });
}

/** Marque une notification lue (idempotent côté serveur). */
export function marquerNotificationLue(id: string): Promise<LectureNotificationUsager> {
  return requete<LectureNotificationUsager>(`/usager/notifications/${id}/lue`, { methode: 'POST' });
}

/** Ouvre un document de l'espace usager par son URL signée (10 minutes), dans un nouvel onglet. */
export async function ouvrirDocument(chemin: string): Promise<string> {
  const r = await requete<{ url: string; expire_le: string }>(chemin);
  window.open(r.url, '_blank', 'noopener');
  return r.url;
}
